import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Spin, message, Tooltip, Icon } from 'antd'
import './FileViewer.scss'
import ReactPlayer from 'react-player'
import getFileIcon from '../utils/getFileIcon'
import { removeFileExtension } from '../utils/removeFileExtension'
import { useTranslation } from 'react-i18next'

const MultiFileViewer = ({
  visible,
  files = [],
  initialIndex = 0,
  onClose,
  onDownload
}) => {
  const [loading, setLoading] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [documentContent, setDocumentContent] = useState(null)
  const [documentError, setDocumentError] = useState(null)
  const currentFile = files[currentIndex]

  const { t } = useTranslation()

  useEffect(() => {
    if (visible && files.length > 0) {
      setLoading(true)
      setCurrentIndex(initialIndex)
      setDocumentContent(null)
      setDocumentError(null)
      setTimeout(() => {
        setLoading(false)
      }, 400)
    }
  }, [visible, files, initialIndex])

  // Load document content when file changes
  useEffect(() => {
    const fileType =
      currentFile &&
      currentFile.media_format &&
      currentFile.media_format.toUpperCase()
    if (currentFile && ['APPLICATION', 'PDF', 'TEXT'].includes(fileType)) {
      loadDocumentContent(currentFile)
    } else {
      setDocumentContent(null)
      setDocumentError(null)
    }
  }, [currentFile])

  const loadDocumentContent = async file => {
    setLoading(true)
    setDocumentError(null)

    try {
      const fileUrl = file.link || `/api/files/${file.path}`
      const response = await fetch(fileUrl)

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`)
      }

      const arrayBuffer = await response.arrayBuffer()
      const fileType = file.media_format && file.media_format.toUpperCase()

      if (fileType === 'DOCX' || fileType === 'DOC') {
        await loadWordDocument(arrayBuffer)
      } else if (fileType === 'XLSX' || fileType === 'XLS') {
        await loadExcelDocument(arrayBuffer)
      }
    } catch (error) {
      console.error('Error loading document:', error)
      setDocumentError(error.message)
      message.error('Failed to load document preview')
    } finally {
      setLoading(false)
    }
  }

  const loadWordDocument = async arrayBuffer => {
    try {
      // Check if mammoth is available via script tag
      if (typeof window !== 'undefined' && window.mammoth) {
        const result = await window.mammoth.convertToHtml({ arrayBuffer })
        setDocumentContent({
          type: 'html',
          content: result.value,
          messages: result.messages
        })
      } else {
        throw new Error(
          'Mammoth library not loaded. Please include mammoth script in your HTML.'
        )
      }
    } catch (error) {
      console.error('Error processing Word document:', error)
      setDocumentError(
        'Word document processing failed. Please download to view.'
      )
    }
  }

  const loadExcelDocument = async arrayBuffer => {
    try {
      // Check if XLSX is available via script tag
      let XLSX
      if (typeof window !== 'undefined' && window.XLSX) {
        XLSX = window.XLSX
      } else {
        throw new Error(
          'XLSX library not loaded. Please include XLSX script in your HTML.'
        )
      }

      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      const sheetNames = workbook.SheetNames
      const sheets = {}

      sheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName]
        const htmlTable = XLSX.utils.sheet_to_html(worksheet, {
          id: `sheet-${sheetName}`,
          editable: false
        })
        sheets[sheetName] = htmlTable
      })

      setDocumentContent({
        type: 'excel',
        sheets: sheets,
        sheetNames: sheetNames,
        activeSheet: sheetNames[0]
      })
    } catch (error) {
      console.error('Error processing Excel document:', error)
      setDocumentError(
        'Excel document processing failed. Please download to view.'
      )
    }
  }

  const isImage = item =>
    item.media_format === 'IMAGE' ||
    item.media_format === 'PEXEL_IMAGE' ||
    item.media_format === 'GIF' ||
    item.media_format === 'CERT' ||
    item.media_format === 'BADGE' ||
    item.media_format === 'AR_IMAGE'
  // Updated usage in your component
  const renderThumbnail = (file, index) => {
    const isActive = index === currentIndex
    const fileTypeCategory = getFileTypeCategory(file.media_format)

    return (
      <div
        className={`thumbnail-item-minimal ${isActive ? 'active' : ''}`}
        key={index}
        data-file-type={fileTypeCategory}
        onClick={() => {
          setCurrentIndex(index)
          setDocumentContent(null)
          setDocumentError(null)
        }}
      >
        {getFileTypeCategory(file.media_format) === 'image' ? (
          <img
            src={file.link || `/api/files/${file.path}`}
            alt={file.name}
            className="thumbnail-image-minimal"
            onError={e => {
              e.target.style.display = 'none'
            }}
          />
        ) : (
          // Use the new icon function with proper parameters
          getFileIcon(file.media_format, file.media_mime_type, 'THUMBNAIL')
        )}
      </div>
    )
  }

  const getFileTypeCategory = mediaFormat => {
    switch (mediaFormat) {
      case 'IMAGE':
      case 'GIF':
      case 'ARIMAGE':
      case 'PEXEL_IMAGE':
      case 'CERT':
      case 'BADGE':
      case 'AR_IMAGE':
        return 'image'
      case 'VIDEO':
      case 'VIDEO_STREAM':
      case 'VIDEO_ANTMEDIA':
        return 'video'
      case 'AUDIO':
        return 'audio'
      case 'APPLICATION':
      case 'PDF':
        return 'document'
      case 'TEXT':
        return 'document'
      default:
        return 'other'
    }
  }

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
      // Reset document content when navigating
      setDocumentContent(null)
      setDocumentError(null)
    }
  }

  const handleNext = () => {
    if (currentIndex < files.length - 1) {
      setCurrentIndex(currentIndex + 1)
      // Reset document content when navigating
      setDocumentContent(null)
      setDocumentError(null)
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'ArrowLeft') {
      handlePrevious()
    } else if (e.key === 'ArrowRight') {
      handleNext()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  useEffect(() => {
    if (visible) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [visible, currentIndex])

  // --- Enhanced Renderers ---
  const renderImageViewer = file => {
    const imageUrl = file.link || `/api/files/${file.path}`
    console.log('Loading image:', imageUrl, 'for file:', file.name, { file })

    return (
      <div className="image-viewer">
        <img
          src={imageUrl}
          alt={file.name}
          className="viewer-image"
          onLoad={() => console.log('Image loaded successfully:', file.name)}
        />
      </div>
    )
  }

  const renderVideoViewer = file => (
    <div className="video-viewer">
      <ReactPlayer
        url={file.link || `/api/files/${file.path}`}
        controls
        className="viewer-video"
        width="100%"
        height="100%"
      />
    </div>
  )

  const renderAudioViewer = file => (
    <div className="audio-viewer">
      <audio
        controls
        className="viewer-audio"
        src={file.link || `/api/files/${file.path}`}
        type={`audio/${file.media_format}`}
      >
        {t('v3:browser_audio_not_support')}
      </audio>
    </div>
  )

  const renderDocumentViewer = file => {
    const fileType = file.media_format
    const isPDF =
      fileType === 'APPLICATION' && file.media_mime_type === 'application/pdf'

    return (
      <div className="document-viewer">
        {isPDF ? (
          <iframe
            src={file.link || `/api/files/${file.path}`}
            className="pdf-viewer"
            title={file.name}
          />
        ) : (
          <div className="document-preview-minimal">
            {getFileIcon(file.media_format, file.media_mime_type)}
            <div style={{ color: '#fff', opacity: 0.7, marginTop: 8 }}>
              {removeFileExtension(file.name, file.media_format)}
            </div>
            <div style={{ color: '#fff', opacity: 0.5, fontSize: 12 }}>
              {t('v5:click_download_to_view')}
            </div>
            <Button
              type="primary"
              onClick={() => onDownload(file)}
              style={{ marginTop: 16 }}
            >
              {t('buttons:download')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  const renderCodeViewer = file => (
    <div className="code-viewer">
      <pre className="code-preview-minimal">
        <code style={{ color: '#fff', background: 'transparent' }}>
          {`// Code preview not available
${removeFileExtension(file.name, file.media_format)}`}
        </code>
      </pre>
    </div>
  )

  const renderArchiveViewer = file => (
    <div className="archive-viewer">
      <Icon
        type={getFileIcon(file.media_format, file.media_mime_type)}
        style={{ fontSize: 48, color: '#fff', opacity: 0.7 }}
      />
      <div style={{ color: '#fff', opacity: 0.7, marginTop: 8 }}>
        {removeFileExtension(file.name, file.media_format)}
      </div>
      <div style={{ color: '#fff', opacity: 0.5, fontSize: 12 }}>
        {t('v5:archive_file')}
      </div>
    </div>
  )

  const renderDefaultViewer = file => (
    <div className="default-viewer">
      <Icon
        type={getFileIcon(file.media_format, file.media_mime_type)}
        style={{ fontSize: 48, color: '#fff', opacity: 0.7 }}
      />
      <div style={{ color: '#fff', opacity: 0.7, marginTop: 8 }}>
        {removeFileExtension(file.name, file.media_format)}
      </div>
      <div style={{ color: '#fff', opacity: 0.5, fontSize: 12 }}>
        {t('v5:no_preview_available')}
      </div>
    </div>
  )

  const renderFileViewer = file => {
    if (!file) return null
    const category = getFileTypeCategory(file.media_format)
    console.log('Rendering file viewer for file:', category, file)

    switch (category) {
      case 'image':
        return renderImageViewer(file)
      case 'video':
        return renderVideoViewer(file)
      case 'audio':
        return renderAudioViewer(file)
      case 'document':
        return renderDocumentViewer(file)
      case 'code':
        return renderCodeViewer(file)
      case 'archive':
        return renderArchiveViewer(file)
      default:
        return renderDefaultViewer(file)
    }
  }

  if (!files || files.length === 0) {
    return null
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered={false}
      width={'100vw'}
      className="multi-file-viewer-modal-modern"
      style={{
        top: 0,
        padding: 0,
        margin: 0,
        maxWidth: '100vw',
        width: '100vw',
        height: '100vh'
      }}
      bodyStyle={{
        background: 'transparent',
        padding: 0,
        margin: 0,
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      destroyOnClose
    >
      <div className="file-viewer-modern-overlay">
        {/* Top Bar */}
        <div className="viewer-top-bar">
          <div className="top-bar-left">
            <Icon
              type={getFileIcon(
                currentFile && currentFile.media_format,
                currentFile && currentFile.media_mime_type
              )}
              style={{ fontSize: 20, color: '#fff', marginRight: 12 }}
            />
            <Tooltip
              title={currentFile && currentFile.title}
              placement="bottom"
            >
              <span className="file-name">
                {currentFile &&
                  removeFileExtension(
                    currentFile.title,
                    currentFile.media_format
                  )}
              </span>
            </Tooltip>
            {files.length > 1 && (
              <span className="file-counter">
                {currentIndex + 1} of {files.length}
              </span>
            )}
          </div>
          <div className="top-bar-right">
            <Tooltip title="Download" placement="bottom">
              <Button
                type="text"
                icon="download"
                size="large"
                onClick={() => onDownload(currentFile)}
                style={{ color: '#fff', border: 'none' }}
              />
            </Tooltip>
            <Tooltip title="Close" placement="bottom">
              <Button
                type="text"
                icon="close"
                size="large"
                onClick={onClose}
                style={{ color: '#fff', border: 'none' }}
              />
            </Tooltip>
          </div>
        </div>

        {/* Navigation Arrows */}
        {files.length > 1 && (
          <>
            <Button
              className="nav-arrow nav-arrow-left"
              type="text"
              icon="left"
              size="large"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            />
            <Button
              className="nav-arrow nav-arrow-right"
              type="text"
              icon="right"
              size="large"
              onClick={handleNext}
              disabled={currentIndex === files.length - 1}
            />
          </>
        )}

        {/* Main Content */}
        <div className="file-viewer-content">
          {loading ? (
            <div className="loading-container">
              <Spin size="large" />
            </div>
          ) : (
            <div className="file-display">{renderFileViewer(currentFile)}</div>
          )}
        </div>

        {/* Bottom Thumbnails */}
        {files.length > 1 && (
          <div className="thumbnail-bar">
            <div className="thumbnail-container">
              {files.map((file, index) => renderThumbnail(file, index))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default MultiFileViewer
